import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

export const Route = createFileRoute("/admin/orders")({
  component: AdminOrders,
});

type Order = {
  id: string;
  full_name: string;
  email: string;
  phone: string;
  city: string;
  total_cents: number;
  status: string;
  created_at: string;
  courier: string | null;
  tracking_number: string | null;
  tracking_url: string | null;
};

const STATUSES = ["pending_payment", "paid", "shipped", "delivered", "cancelled"] as const;

function AdminOrders() {
  const [orders, setOrders] = useState<Order[]>([]);
  const [saving, setSaving] = useState<string | null>(null);

  const load = async () => {
    const { data, error } = await supabase.from("orders").select("*").order("created_at", { ascending: false });
    if (error) toast.error(error.message);
    else setOrders((data as Order[]) ?? []);
  };

  useEffect(() => { load(); }, []);

  const update = async (id: string, patch: Record<string, unknown>) => {
    setSaving(id);
    const { error } = await supabase.from("orders").update(patch as never).eq("id", id);
    setSaving(null);
    if (error) return toast.error(error.message);
    toast.success("Order updated");
    setOrders((prev) => prev.map((o) => (o.id === id ? { ...o, ...patch } : o)));
  };

  return (
    <div className="space-y-4">
      {orders.length === 0 && (
        <p className="p-5 text-sm text-muted-foreground border border-border rounded-lg bg-card">No orders yet.</p>
      )}
      {orders.map((o) => (
        <div key={o.id} className="rounded-lg border border-border bg-card p-4 space-y-3">
          <div className="flex flex-wrap items-start gap-4">
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium">{o.full_name} · {o.city}</p>
              <p className="text-xs text-muted-foreground">{o.email} · {o.phone}</p>
              <p className="text-xs text-muted-foreground">{new Date(o.created_at).toLocaleString()}</p>
            </div>
            <div className="text-right">
              <p className="text-sm font-medium">₹{(o.total_cents / 100).toFixed(0)}</p>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-4 gap-2">
            <label className="text-xs space-y-1">
              <span className="text-muted-foreground">Status</span>
              <select
                className="w-full border border-border bg-background px-2 py-1.5 rounded text-sm"
                value={o.status}
                onChange={(e) => update(o.id, { status: e.target.value })}
                disabled={saving === o.id}
              >
                {STATUSES.map((s) => <option key={s} value={s}>{s.replace("_", " ")}</option>)}
              </select>
            </label>
            <label className="text-xs space-y-1">
              <span className="text-muted-foreground">Courier</span>
              <input
                className="w-full border border-border bg-background px-2 py-1.5 rounded text-sm"
                defaultValue={o.courier ?? ""}
                placeholder="e.g. DTDC"
                onBlur={(e) => e.target.value !== (o.courier ?? "") && update(o.id, { courier: e.target.value || null })}
              />
            </label>
            <label className="text-xs space-y-1">
              <span className="text-muted-foreground">Tracking number</span>
              <input
                className="w-full border border-border bg-background px-2 py-1.5 rounded text-sm"
                defaultValue={o.tracking_number ?? ""}
                placeholder="AWB / Tracking ID"
                onBlur={(e) => e.target.value !== (o.tracking_number ?? "") && update(o.id, { tracking_number: e.target.value || null })}
              />
            </label>
            <label className="text-xs space-y-1">
              <span className="text-muted-foreground">Tracking URL</span>
              <input
                className="w-full border border-border bg-background px-2 py-1.5 rounded text-sm"
                defaultValue={o.tracking_url ?? ""}
                placeholder="https://..."
                onBlur={(e) => e.target.value !== (o.tracking_url ?? "") && update(o.id, { tracking_url: e.target.value || null })}
              />
            </label>
          </div>
        </div>
      ))}
    </div>
  );
}
